import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { heureka } from "@otto-ec/heureka";
import type { Appendable } from "../util/Appendable";
import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
import { dereference, readLabel } from "../tracking/FeatureTracking";
import {
  isNba,
  isNotSelected,
  isSelected,
  setFeatureStatus,
  SvelteFacetValue,
  updateFacetValueTagPosition,
  updateNBAPosition,
  updateSanTopFacetValue,
} from "../filter/SvelteFacetValue";
import { clone, element } from "../util/Utils";
import { isMobile } from "../util/Breakpoint";
import { events } from "../events";
import tracker from "../tracking/Tracker";
import { setChangeLabels } from "../filter/FilterTracking";
import { trackInAllEvents } from "../tracking/GlobalTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import { SvelteFacet } from "../filter/SvelteFacet";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { RefinementBar } from "../refinementBar/RefinementBar";

const CLASS_NEXT_BEST_ACTION_WRAPPER = "hc_nextBestAction";
const CLASS_DESELECT_ALL = "hc_deselectAll";
const SELECTOR_DESELECT_ALL = `.${CLASS_DESELECT_ALL}`;
const SELECTOR_TEMPLATE_FACET_VALUE_TAGS = `.hc_svelteNextBestActionWrapper .${CLASS_NEXT_BEST_ACTION_WRAPPER}`;
const CATEGORY_PATH_FACET_NAME = "kategorien";

export class NextBestActionComponent implements Appendable {
  static readonly factory = HeurekaElementFactory.byClass(CLASS_NEXT_BEST_ACTION_WRAPPER, NextBestActionComponent);

  /*               */

  constructor(readonly tags: HTMLElement) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return NextBestActionComponent.factory.pick(SELECTOR_TEMPLATE_FACET_VALUE_TAGS, rootElement);
  }

  /*               */

  init() {
    this.handleSelectedCategoryPathFacetValues();
    this.registerListener();
    this.deselectAllButton?.addEventListener("click", () => {
      /*                                                                                         */
      this.selectedValues.forEach((tag) => {
        tag.updateTrackingLabelsOnReferenced("deleted", "x_all_top");
      });
    });
    this.selectedValues.forEach((tag) => {
      tag.facetValue.addEventListener("click", () => {
        /*                                                                                   */
        tag.updateTrackingLabelsOnReferenced("deleted", "x_single_top");
      });
    });
    return this;
  }

  static register() {
    if (NextBestActionComponent.isVisible()) {
      events.onFilterSectionLoaded(NextBestActionComponent.initAll);
    }
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    NextBestActionComponent.emitSelectedFacetValues(event, rootElement);
  }

  private static emitSelectedFacetValues(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    const refinementBar = RefinementBar.facetValueTagsElement(rootElement);
    if (refinementBar) {
      const nba = NextBestActionComponent.factory.pick(undefined, refinementBar);
      const selectedValues = nba?.selectedValues.map((value) => value.title);
      if (selectedValues && selectedValues.length) {
        events.facetValueTagsLoaded(event.rule, selectedValues);
      }
    }
  }

  /*                       */

  clone() {
    return NextBestActionComponent.factory.create(clone(this.tags)).init();
  }

  get deselectAllButton(): HTMLElement | null {
    return element(SELECTOR_DESELECT_ALL, this.tags);
  }

  get topQuickFacet(): string | undefined {
    return this.tags.dataset.tsTopQuickFacet;
  }

  get values(): SvelteFacetValue[] {
    return SvelteFacetValue.factory.all(this.tags);
  }

  get selectedValues(): SvelteFacetValue[] {
    return this.values.filter(isSelected);
  }

  get deselectAll(): HTMLFormElement | undefined {
    return this.tags.querySelector(SELECTOR_DESELECT_ALL);
  }

  appendTo(elem: HTMLElement): void {
    elem.appendChild(this.tags);
  }

  removeNonSelectedNbaWithRef(ref: string): void {
    const value = this.values.find((value) => value.ref === ref && !value.checked);
    value?.liElem?.remove();
  }

  static isVisible(): boolean {
    return isMobile();
  }

  static isNotVisible(): boolean {
    return !NextBestActionComponent.isVisible();
  }

  static hideBanner(): boolean {
    const facetValueTagsTemplate = NextBestActionComponent.template();
    return isMobile() && facetValueTagsTemplate && facetValueTagsTemplate.values.length > 0;
  }

  /*                 */

  private handleChange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  registerListener() {
    this.tags.addEventListener("change", this.handleChange);
    this.deselectAll?.addEventListener("submit", (event) => {
      this.values.forEach((value) => {
        const referenced = dereference(value.facetValue);
        clearFormInput(referenced);
      });
      interceptSubmit(event);
    });
  }

  /*       */

  private handleSelectedCategoryPathFacetValues() {
    const selectedCategoryPathNbaValues = this.values.filter(
      (value) => value.checked && value.name === CATEGORY_PATH_FACET_NAME,
    );
    const selectedCategoryPathNbaValuesIds = selectedCategoryPathNbaValues.map(
      (value) => value.facetValue.dataset.tsFeatureRef,
    );
    const categoryPathNbaTemplate = selectedCategoryPathNbaValues?.[0];
    if (categoryPathNbaTemplate) {
      const categoryPathFacet = SvelteFacet.facetName("kategorien");
      const categoryPathSelectedValues =
        categoryPathFacet?.selectedValues?.filter((value) => {
          return !selectedCategoryPathNbaValuesIds.includes(value.id);
        }) || [];
      const parentNode = categoryPathNbaTemplate.liElem.parentNode;
      categoryPathSelectedValues.forEach((value) => {
        this.removeNonSelectedNbaWithRef(value.id);
        const clonedNbaValue = categoryPathNbaTemplate.cloneWithData(value.id, value.spanInnerHTML);
        const firstSibling = categoryPathNbaTemplate.liElem.nextSibling;
        parentNode.insertBefore(clonedNbaValue.liElem, firstSibling);
      });
    }
  }
}

function trackNba() {
  const facetValueTagsTemplate = NextBestActionComponent.template();
  if (facetValueTagsTemplate) {
    facetValueTagsTemplate.selectedValues.forEach(updateFacetValueTagPosition);
    facetValueTagsTemplate.values.filter(isNba).forEach((value, index) => {
      updateNBAPosition(value, index);
      /*                                                                                                          */
      updateSanTopFacetValue(value);
    });
    /*                                                               */
    facetValueTagsTemplate.values.filter(isNotSelected).forEach((value) => {
      setFeatureStatus(value, "visible");
    });
    const { tsLabels } = facetValueTagsTemplate.tags.dataset || {};
    if (tsLabels) {
      tracker.addToPageImpression(JSON.parse(tsLabels));
    }
  }
}

/*                                                                                                        */
function trackNbaTopQuickFacet() {
  const facetValueTagsTemplate = NextBestActionComponent.template();
  if (facetValueTagsTemplate) {
    const topQuickFacet = facetValueTagsTemplate.topQuickFacet;
    if (topQuickFacet) {
      trackInAllEvents("san_TopQuickFacet", topQuickFacet);
    }
  }
}

export function registerNextBestActionComponentHandlers() {
  if (NextBestActionComponent.isVisible()) {
    events.onFilterSectionLoaded(trackNba);
    /*                                          */
    /*                                                                                                      */
    /*                                          */
    events.onFilterSheetOpen(trackNba);
    events.onFilterSectionLoaded(trackNbaTopQuickFacet);
  }
}

export function updateNbaValuesStatusTo(status: FeatureStatus) {
  const facetValueTagsTemplate = NextBestActionComponent.template();
  if (facetValueTagsTemplate) {
    facetValueTagsTemplate.values.filter(isNba).forEach((value) => {
      setFeatureStatus(value, status);
    });
  }
}
